// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Version Diff View
// ═══════════════════════════════════════════════════════════════

import { C, formatDate } from '../../../../../lib/design';
import { useI18n } from '../../../../../i18n';
import type { DocumentVersion } from '../../../../../types';

// ─── ProseMirror JSON → text lines ───

interface ProseMirrorNode {
  type: string;
  content?: ProseMirrorNode[];
  text?: string;
  attrs?: Record<string, any>;
}

/** Extract text blocks from ProseMirror JSON. Each paragraph/heading becomes one line. */
function extractTextLines(doc: any): string[] {
  if (!doc || !doc.content) return [];
  const lines: string[] = [];

  function nodeToText(node: ProseMirrorNode): string {
    if (node.text) return node.text;
    if (!node.content) return '';
    return node.content.map(nodeToText).join('');
  }

  for (const node of doc.content as ProseMirrorNode[]) {
    const text = nodeToText(node).trim();
    // Include all block nodes, even empty ones (they represent blank lines)
    lines.push(text);
  }

  return lines;
}

// ─── LCS-based diff algorithm ───

export type DiffStatus = 'added' | 'removed' | 'unchanged';
export interface DiffLine {
  text: string;
  status: DiffStatus;
}

/** Compute LCS length table */
function lcsTable(a: string[], b: string[]): number[][] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  return dp;
}

/** Produce a unified diff from two arrays of lines using LCS backtracking */
export function computeDiff(oldLines: string[], newLines: string[]): DiffLine[] {
  const dp = lcsTable(oldLines, newLines);
  const result: DiffLine[] = [];

  let i = oldLines.length;
  let j = newLines.length;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      result.push({ text: oldLines[i - 1], status: 'unchanged' });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.push({ text: newLines[j - 1], status: 'added' });
      j--;
    } else {
      result.push({ text: oldLines[i - 1], status: 'removed' });
      i--;
    }
  }

  return result.reverse();
}

// ─── Component ───

export interface VersionDiffProps {
  versionA: DocumentVersion; // older
  versionB: DocumentVersion; // newer (current)
  onClose: () => void;
}

export function VersionDiff({ versionA, versionB, onClose }: VersionDiffProps) {
  const { t } = useI18n();
  const linesA = extractTextLines(versionA.contenidoJson);
  const linesB = extractTextLines(versionB.contenidoJson);
  const diff = computeDiff(linesA, linesB);

  const stats = {
    added: diff.filter((d) => d.status === 'added').length,
    removed: diff.filter((d) => d.status === 'removed').length,
    unchanged: diff.filter((d) => d.status === 'unchanged').length,
  };

  return (
    <div style={{ background: '#ECEEF2', padding: '24px 28px', minHeight: 'calc(100vh - 200px)' }}>
      <div
        style={{
          maxWidth: 820,
          margin: '0 auto',
          background: 'white',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            background: `linear-gradient(135deg, ${C.azul1}, ${C.azulMedio})`,
            color: 'white',
          }}
        >
          <div>
            <h2
              style={{
                fontSize: 16,
                fontWeight: 700,
                margin: 0,
                fontFamily: "'Source Serif 4',Georgia,serif",
              }}
            >
              {t.diff.comparacionVersiones}
            </h2>
            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>
              Edición {String(versionA.numeroEdicion).padStart(2, '0')} (
              {formatDate(versionA.fechaAprobacion ?? versionA.creadoEn)}){' → '}
              Edición {String(versionB.numeroEdicion).padStart(2, '0')} (
              {formatDate(versionB.fechaAprobacion ?? versionB.creadoEn)})
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: 6,
              color: 'white',
              cursor: 'pointer',
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.25)')}
            onMouseOut={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
          >
            {t.diff.cerrar}
          </button>
        </div>

        {/* Stats bar */}
        <div
          style={{
            display: 'flex',
            gap: 16,
            padding: '10px 24px',
            borderBottom: `1px solid ${C.border}`,
            fontSize: 12,
            color: C.textSec,
          }}
        >
          <span style={{ color: '#16A34A', fontWeight: 600 }}>
            +{stats.added} {t.diff.anadidasLabel}
          </span>
          <span style={{ color: '#DC2626', fontWeight: 600 }}>
            −{stats.removed} {t.diff.eliminadasLabel}
          </span>
          <span>
            {stats.unchanged} {t.diff.sinCambios}
          </span>
        </div>

        {/* Diff content */}
        <div style={{ padding: '16px 24px', maxHeight: 'calc(100vh - 340px)', overflowY: 'auto' }}>
          {diff.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: C.textMut, fontSize: 13 }}>
              {t.diff.sinContenido}
            </div>
          ) : (
            <div
              style={{
                fontFamily: "'Source Sans 3', 'Segoe UI', sans-serif",
                fontSize: 13,
                lineHeight: 1.7,
              }}
            >
              {diff.map((line, idx) => {
                const bg =
                  line.status === 'added'
                    ? '#F0FDF4'
                    : line.status === 'removed'
                      ? '#FEF2F2'
                      : 'transparent';
                const borderLeft =
                  line.status === 'added'
                    ? '3px solid #16A34A'
                    : line.status === 'removed'
                      ? '3px solid #DC2626'
                      : '3px solid transparent';
                const color =
                  line.status === 'removed'
                    ? '#991B1B'
                    : line.status === 'added'
                      ? '#166534'
                      : C.text;
                const prefix =
                  line.status === 'added' ? '+ ' : line.status === 'removed' ? '− ' : '  ';
                const textDecoration = line.status === 'removed' ? 'line-through' : 'none';

                return (
                  <div
                    key={idx}
                    style={{
                      background: bg,
                      borderLeft,
                      padding: '2px 12px',
                      color,
                      textDecoration,
                      minHeight: 24,
                    }}
                  >
                    <span
                      style={{
                        opacity: 0.5,
                        userSelect: 'none',
                        marginRight: 8,
                        fontSize: 11,
                        fontFamily: 'monospace',
                      }}
                    >
                      {prefix}
                    </span>
                    {line.text || '\u00A0'}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Legend */}
        <div
          style={{
            display: 'flex',
            gap: 20,
            padding: '12px 24px',
            borderTop: `1px solid ${C.border}`,
            fontSize: 11,
            color: C.textMut,
          }}
        >
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                background: '#F0FDF4',
                border: '1px solid #16A34A',
                borderRadius: 2,
                marginRight: 4,
                verticalAlign: 'middle',
              }}
            />{' '}
            Línea añadida
          </span>
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                background: '#FEF2F2',
                border: '1px solid #DC2626',
                borderRadius: 2,
                marginRight: 4,
                verticalAlign: 'middle',
              }}
            />{' '}
            Línea eliminada
          </span>
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                background: '#F8F9FB',
                border: '1px solid #E2E6ED',
                borderRadius: 2,
                marginRight: 4,
                verticalAlign: 'middle',
              }}
            />{' '}
            Sin cambios
          </span>
        </div>
      </div>
    </div>
  );
}
